import type { EmbedPublicData } from './resolve-embed';

const apiBase = (import.meta.env.VITE_API_BASE as string | undefined) ?? 'http://localhost:8787';

interface QueueItem {
  embed_id: string;
  session_id: string;
  kind: string;
  payload: Record<string, unknown>;
  ts: string;
}

const sessionId = crypto.randomUUID();
const queue: QueueItem[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let embedJwt: string | null = null;

export function mountAnalytics(embed: EmbedPublicData): void {
  embedJwt = readCookie('hs_embed_token');
  emit(embed, 'view_start', {});

  document.addEventListener('hs-viewer-action', (ev: Event) => {
    const detail = (ev as CustomEvent<{ action: string }>).detail;
    if (detail.action === 'measure') emit(embed, 'measure', {});
    if (detail.action === 'plan') emit(embed, 'cta_click', { cta: 'plan' });
  });

  window.addEventListener('beforeunload', () => {
    emit(embed, 'view_end', { duration_sec: Math.floor(performance.now() / 1000) });
    flush(true);
  });
}

function emit(embed: EmbedPublicData, kind: string, payload: Record<string, unknown>) {
  // we kennen alleen scan_id publiek; embed_id wordt op server-side resolved via token
  queue.push({
    embed_id: embed.scan_id, // placeholder; productie: zet embed_id in publieke payload toe
    session_id: sessionId,
    kind,
    payload,
    ts: new Date().toISOString(),
  });
  if (queue.length >= 10) flush();
  else if (!flushTimer) flushTimer = setTimeout(flush, 5000);
}

async function flush(useBeacon = false): Promise<void> {
  if (queue.length === 0) return;
  const batch = { events: queue.splice(0, 10) };
  flushTimer = null;
  const url = `${apiBase}/v1/analytics/ingest`;
  if (useBeacon && navigator.sendBeacon) {
    navigator.sendBeacon(url, JSON.stringify(batch));
    return;
  }
  try {
    await fetch(url, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        ...(embedJwt ? { 'x-embed-token': embedJwt } : {}),
      },
      body: JSON.stringify(batch),
      keepalive: true,
    });
  } catch (err) {
    console.warn('analytics flush failed', err);
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[2]!) : null;
}
